import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getAllPosts, getVolunteerSpotlight, formatDate } from '../lib/dataService';
import NewsCard from '../components/NewsCard';
import NewsletterSignup from '../components/NewsletterSignup';
import { ChevronLeft, ChevronRight, ArrowRight, Play, Users, CalendarDays, BarChart3, BookOpen, Trophy, HeartHandshake, Quote } from 'lucide-react';

const QUICK_LINKS = [
  { icon: Users, title: 'About Us', desc: 'Learn more about who we are and what we do.', to: '/about-us' },
  { icon: CalendarDays, title: 'Events', desc: 'Check out our calendar of events.', to: '/page/events' },
  { icon: BarChart3, title: 'Results & Stats', desc: 'Results & stats from our various leagues.', href: 'http://ciba.web.geniussports.com/competitions/?cu=CYBA/schedule' },
  { icon: BookOpen, title: 'Rules & Regulations', desc: 'Know the rules of the game before you compete.', to: '/page/rules-and-regulations' },
];

const LEAGUES = [
  { name: "National Men's League", to: '/page/national-mens-basketball-league' },
  { name: "National Women's League", to: '/page/national-womens-league' },
  { name: 'Under 19 Boys & Girls', to: '/page/under-19' },
  { name: 'Under 16 Boys & Girls', to: '/page/under16' },
  { name: '15 & Under Boys & Girls', to: '/page/ciba-15-under-boys-girls-basketball-leagues' },
  { name: '13 & Under Boys & Girls', to: '/page/under-13' },
  { name: '10 & Under Boys & Girls', to: '/page/under-10' },
  { name: 'Corporate League', to: '/page/corporate-basketball-league' },
  { name: 'Kings Development Program', to: '/page/kings-development-program' },
];

export default function HomePage() {
  const [posts, setPosts] = useState([]);
  const [current, setCurrent] = useState(0);
  const [spotlight, setSpotlight] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    getAllPosts().then(setPosts);
    getVolunteerSpotlight().then((s) => setSpotlight(s?.active ? s : null)).catch(() => setSpotlight(null));
  }, []);

  const heroPosts = posts.slice(0, 5);
  const gridPosts = posts.slice(5, 14);

  const next = useCallback(() => setCurrent((c) => (c + 1) % Math.max(heroPosts.length, 1)), [heroPosts.length]);
  const prev = () => setCurrent((c) => (c - 1 + heroPosts.length) % heroPosts.length);

  useEffect(() => {
    if (heroPosts.length < 2) return;
    timerRef.current = setInterval(next, 6000);
    return () => clearInterval(timerRef.current);
  }, [heroPosts.length, next, current]);

  return (
    <div className="home-page">
      {/* ===== HERO NEWS SLIDER ===== */}
      <section className="hero-slider" aria-label="Top stories">
        {heroPosts.map((post, i) => (
          <div key={post.slug} className={`hero-slide ${i === current ? 'active' : ''}`}>
            <div className="hero-slide-bg" style={{ backgroundImage: `url(${post.image})` }} />
            <div className="hero-slide-overlay" />
            <div className="container hero-slide-content">
              <div className="hero-slide-inner">
                {post.categories && post.categories[0] && (
                  <span className="hero-cat">{post.categories[0]}</span>
                )}
                <h1 className="hero-title">{post.title}</h1>
                <p className="hero-excerpt">{post.excerpt}</p>
                <div className="hero-meta">
                  <span className="hero-date">{formatDate(post.date)}</span>
                  <Link to={`/news/${post.slug}`} className="btn-primary">
                    Read Full Story <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
        {heroPosts.length > 1 && (
          <>
            <button className="hero-arrow left" onClick={prev} aria-label="Previous story"><ChevronLeft size={26} /></button>
            <button className="hero-arrow right" onClick={next} aria-label="Next story"><ChevronRight size={26} /></button>
            <div className="hero-dots">
              {heroPosts.map((_, i) => (
                <button key={i} className={`hero-dot ${i === current ? 'active' : ''}`} onClick={() => setCurrent(i)} aria-label={`Slide ${i + 1}`} />
              ))}
            </div>
          </>
        )}
      </section>

      {/* ===== QUICK LINKS ===== */}
      <section className="quick-links">
        <div className="container quick-links-grid">
          {QUICK_LINKS.map((q) => {
            const Icon = q.icon;
            const inner = (
              <>
                <div className="quick-icon"><Icon size={26} /></div>
                <div>
                  <h3>{q.title}</h3>
                  <p>{q.desc}</p>
                </div>
              </>
            );
            return q.href ? (
              <a key={q.title} href={q.href} target="_blank" rel="noreferrer" className="quick-card">{inner}</a>
            ) : (
              <Link key={q.title} to={q.to} className="quick-card">{inner}</Link>
            );
          })}
        </div>
      </section>

      {/* ===== LATEST NEWS GRID ===== */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <h2 className="section-title">Latest News</h2>
            <Link to="/news" className="section-link">All News <ArrowRight size={16} /></Link>
          </div>
          <div className="news-grid">
            {gridPosts.map((post) => (
              <NewsCard key={post.slug} post={post} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== DOCUMENTARY CTA ===== */}
      <section className="video-cta">
        <div className="container video-cta-inner">
          <div className="video-cta-text">
            <span className="video-cta-label">Our Story</span>
            <h2>You can't know where you are going unless you know where you are coming from.</h2>
            <p>Check out this documentary of Cayman Basketball and discover the history behind the game on our islands.</p>
            <Link to="/page/history-video" className="btn-light">
              <Play size={16} /> Watch the Documentary
            </Link>
          </div>
        </div>
      </section>

      {/* ===== LEAGUES ===== */}
      <section className="section leagues-section">
        <div className="container">
          <div className="section-head">
            <h2 className="section-title">Our Leagues & Programs</h2>
          </div>
          <p className="leagues-intro">CIBA organizes leagues for every age group annually, serving more than 1,500 active athletes across the Cayman Islands.</p>
          <div className="leagues-grid">
            {LEAGUES.map((l) => (
              <Link key={l.name} to={l.to} className="league-card">
                <Trophy size={20} />
                <span>{l.name}</span>
                <ArrowRight size={16} className="league-arrow" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== VOLUNTEER SPOTLIGHT ===== */}
      {spotlight && (
        <section className="spotlight-band" data-testid="volunteer-spotlight-section">
          <div className="container spotlight-inner">
            <div className="spotlight-media">
              {spotlight.photo ? (
                <img src={spotlight.photo} alt={spotlight.name} className="spotlight-photo" />
              ) : (
                <div className="spotlight-photo spotlight-photo-fallback"><HeartHandshake size={54} /></div>
              )}
            </div>
            <div className="spotlight-text">
              <span className="spotlight-label">
                Volunteer of the Month{spotlight.month ? ` — ${spotlight.month}` : ''}
              </span>
              <h2 data-testid="spotlight-name">{spotlight.name}</h2>
              {spotlight.role && <span className="spotlight-role">{spotlight.role}</span>}
              {spotlight.quote && (
                <p className="spotlight-quote"><Quote size={16} /> {spotlight.quote}</p>
              )}
              <div className="spotlight-actions">
                <Link to="/volunteer" className="btn-primary" data-testid="spotlight-volunteer-cta">
                  Become a Volunteer <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== NEWSLETTER ===== */}
      <NewsletterSignup />

      {/* ===== SPONSORS BAND ===== */}
      <section className="sponsors-band">
        <div className="container">
          <span className="sponsors-label">Proudly supported by</span>
          <div className="sponsors-names">
            <span>KPMG</span>
            <span>PwC</span>
            <span>Deloitte</span>
            <span>RBC</span>
            <span>Mourant</span>
            <span>Vistra</span>
          </div>
          <Link to="/page/sponsors" className="section-link">View All Sponsors <ArrowRight size={14} /></Link>
        </div>
      </section>
    </div>
  );
}
